// MACHINERY V2 (Stroje) — Dokumenty tab: file tile + one document row (M2.5).
//
// Spec: data package §6 (Manuals & Software). Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`PtFileIcon`,
// `DocRow`); artboards 1003 (desktop) and 1014 (phone).
//
// A DOCUMENT MAY HAVE NO FILE. `DocumentViewModel.File` is nullable (§6), and
// the record is still a real document — it just has nothing to download. The
// row therefore renders a neutral tile, an explanatory subtitle, no size and no
// Download affordance. It does NOT render a broken download button.
//
// SIZE "—" vs "0 B" is FIX 2 and the distinction is load-bearing; the whole
// derivation lives in tabFormat.ts.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Document } from "../../../models/machineDocumentsModel";
import FleetCardMenu, { FleetMenuAction } from "../../fleet/FleetCardMenu";
import { fileExtension, formatDocSize, formatTabDate } from "./tabFormat";

/** The little document tile. `?` when the document carries no file. */
export const FileTile: React.FC<{ fileName?: string | null }> = ({ fileName }) => {
  const ext = fileExtension(fileName);
  return (
    <span
      aria-hidden
      style={{
        width: 34,
        height: 40,
        flexShrink: 0,
        borderRadius: 6,
        background: "var(--bg-soft)",
        border: "1px solid var(--border)",
        display: "grid",
        placeItems: "center",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: ext ? "var(--mod-accent)" : "var(--faint)",
        }}
      >
        {ext || "?"}
      </span>
    </span>
  );
};

const DownloadGlyph: React.FC = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden>
    <line x1="6" y1="1.5" x2="6" y2="8" stroke="currentColor" strokeLinecap="round" />
    <path d="M3.5 5.8 L6 8.3 L8.5 5.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="2" y1="10.3" x2="10" y2="10.3" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

export interface DocRowProps {
  document: Document;
  actions: FleetMenuAction[];
  onDownload: (doc: Document) => void;
  menuLabel: string;
  phone?: boolean;
}

/**
 * One document. Desktop is the artboard-1003 five-column row; phone (1014)
 * drops the separate size/date columns into one mono meta line and turns the
 * text Download link into a square icon button, because a 44px touch target
 * next to a kebab does not fit a word.
 */
export const DocRow: React.FC<DocRowProps> = ({
  document: doc,
  actions,
  onDownload,
  menuLabel,
  phone,
}) => {
  const { translate } = useTranslation("myMachinery");
  const file = doc.file;
  const size = file ? formatDocSize(file.size) : null;
  const created = formatTabDate(doc.createdDate, !phone);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: phone ? 10 : 13,
        padding: phone ? "10px 12px" : "10px 14px",
        borderTop: "1px solid var(--border-soft)",
        minWidth: 0,
      }}
    >
      <FileTile fileName={file?.name} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--ink)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={doc.name}
        >
          {doc.name}
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            color: file ? "var(--muted)" : "var(--faint)",
            marginTop: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={file?.name || undefined}
        >
          {file
            ? phone
              ? [size, created].filter(Boolean).join("  ·  ")
              : file.name
            : translate("myMachinery:v2.tabs.docs.noFile")}
        </div>
      </div>

      {!phone && (
        <>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
              width: 64,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {file ? size : ""}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
              width: 124,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {created}
          </span>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        {file ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onDownload(doc)}
            aria-label={translate("common:buttons.download")}
            title={translate("common:buttons.download")}
            style={{
              color: "var(--mod-accent)",
              fontWeight: 600,
              height: phone ? 32 : 26,
              width: phone ? 32 : undefined,
              padding: phone ? 0 : "0 9px",
              justifyContent: "center",
              whiteSpace: "nowrap",
            }}
          >
            {phone ? (
              <DownloadGlyph />
            ) : (
              <>
                {translate("common:buttons.download")}
                <span aria-hidden style={{ marginLeft: 5 }}>
                  <DownloadGlyph />
                </span>
              </>
            )}
          </button>
        ) : (
          !phone && <span style={{ width: 92 }} />
        )}
        <FleetCardMenu actions={actions} ariaLabel={menuLabel} />
      </div>
    </div>
  );
};
